import { BalanceDataInterface } from '@/interfaces/balanceSheet';
import { useGetAccountsQuery } from '@/store/accountsApi';
import {
  accountsToBalanceData,
  filterAccounts,
  AccountFilters,
} from '@/utils/accountsToBalanceData';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { useAuthIsLoaded } from './useAuthIsLoaded';

const emptyBalanceData: BalanceDataInterface = {
  assets: { total: 0, categories: [] },
  liabilities: { total: 0, categories: [] },
  totalLiabilitiesAndEquity: 0,
};

interface UseBalanceSheetDataResult {
  balanceData: BalanceDataInterface;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
  /** True once a response has resolved and it contained no accounts. */
  isEmpty: boolean;
  /** True before the query has started (auth not yet loaded). */
  isUninitialized: boolean;
}

export const useBalanceSheetData = (
  asOfDate?: string,
  investorId?: number,
  filters?: AccountFilters,
  groupId?: number,
  attempt = 0,
): UseBalanceSheetDataResult => {
  const authLoaded = useAuthIsLoaded();
  const { data, isFetching, isError, isUninitialized, refetch } =
    useGetAccountsQuery(
      authLoaded
        ? {
            showClosed: false,
            asOfDate,
            ...(typeof investorId === 'number'
              ? { InvestorId: investorId }
              : {}),
            ...(typeof groupId === 'number' ? { GroupId: groupId } : {}),
            OnlyPrimaryInvestors: false,
            ...(attempt > 0 ? { retryAttempt: attempt } : {}),
          }
        : skipToken,
    );

  const records = data?.Records;

  const balanceData = useMemo(() => {
    if (!records) return emptyBalanceData;

    // Filter accounts if filters are provided
    const accountsToProcess = filters
      ? filterAccounts(records, filters)
      : records;

    return accountsToBalanceData(accountsToProcess);
  }, [records, filters]);

  const isEmpty = !!records && records.length === 0;

  return {
    balanceData,
    isFetching,
    isError,
    refetch: () => {
      void refetch();
    },
    isEmpty,
    isUninitialized,
  };
};
